import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, FC } from 'react';

import useRouter from 'hooks/useRouter';
import useTabNavActive from 'hooks/useTabNavActive';
import { CurrentUserContext } from './CurrentUserContext';
import { getCurrentRoleGrants } from 'selectors';
import { hasAccessPermission } from 'utils';
import {
  buildRecordPath,
  TAB_RECORD_SECTION,
  buildTabKey,
  buildTabLabel,
  getSectionFromPath,
  isTabHostRecordType,
  matchRecordPath,
  MAX_TABS,
  TabRecordType,
  TabSection,
  TabType,
  TAB_RECORD_MODULE,
  TAB_SECTION_LABEL,
  TAB_SECTION_PATH
} from 'constants/tabs';

/**
 * A section *inside* a record page, asked for by the link that opened it — the client list's
 * "View Jobs" lands on the client's Jobs section rather than on Profile.
 *
 * `at` is the clock value when it was asked for. It carries no meaning of its own; it changes on
 * every request, which is what lets the page tell a fresh ask apart from the one it already acted
 * on, so the link still works on a record that is already open in a tab.
 */
export interface InnerTabRequest {
  index: number;
  at: number;
}

export interface TabItem {
  /** `${type}:${recordId}` — one record of one type never has two tabs (TAB-05). */
  key: string;
  type: TabType;
  recordId?: string;
  label: string;
  /** The section list view. Always the first tab, never closable (TAB-01, TAB-04). */
  pinned: boolean;
  innerTab?: InnerTabRequest;
  /** Bumped by refreshTab; TabHost keys the pane on it, so a bump remounts the page and it reloads. */
  version?: number;
}

interface TabState {
  section?: TabSection;
  tabs: TabItem[];
  activeKey: string;
  /** Briefly set when an already open tab is focused instead of duplicated (TAB-05). */
  flashKey?: string;
  /** Something happened the user should know about, such as a tab closed to stay under the limit. */
  notice?: string;
  /** Bumped to force the pinned list tab to reload after a record it lists has been deleted. */
  pinnedVersion: number;
  clock: number;
}

type TabAction =
  | { type: 'SYNC_SECTION'; section?: TabSection }
  | { type: 'OPEN_TAB'; recordType: TabRecordType; recordId: string; label: string; innerTab?: number }
  | { type: 'ACTIVATE_TAB'; key: string }
  | { type: 'CLOSE_TAB'; key: string }
  | { type: 'UPDATE_LABEL'; key: string; label: string }
  | { type: 'CLEAR_RECORD_TABS' }
  | { type: 'CLEAR_FLASH' }
  | { type: 'CLEAR_NOTICE' }
  | { type: 'REFRESH_PINNED' }
  | { type: 'REFRESH_TAB'; key: string }
  | { type: 'RESET_TO_PATH'; pathname: string };

export interface OpenTabParams {
  recordType: TabRecordType;
  recordId: string | number;
  /** Record name for client tabs; ignored for the id based labels. */
  label?: string;
  /** Section to open the record on, for links that point at one (client detail's Jobs, Quotations). */
  innerTab?: number;
}

export interface TabContextData {
  isTabNavActive: boolean;
  section?: TabSection;
  tabs: TabItem[];
  activeKey: string;
  flashKey?: string;
  activeTab?: TabItem;
  /** True when this user may open a tab of that type — mirrors the ConditionalRoute guard. */
  canOpenTab: (recordType: TabRecordType) => boolean;
  /** Returns false when the caller should fall back to normal navigation. */
  openTab: (params: OpenTabParams) => boolean;
  activateTab: (key: string) => void;
  closeTab: (key: string) => void;
  updateTabLabel: (key: string, label: string) => void;
  clearRecordTabs: () => void;
  /** Message to surface to the user, currently only raised by the tab limit. */
  notice?: string;
  dismissNotice: () => void;
  /** Remounts the pinned list tab so it reloads — used after a record it lists is deleted. */
  refreshPinnedTab: () => void;
  /** Remounts one tab so it loads afresh, as a browser reload would. Unsaved changes are lost. */
  refreshTab: (key: string) => void;
  pinnedVersion: number;
}

const buildPinnedTab = (section: TabSection): TabItem => ({
  key: buildTabKey('LIST', section),
  type: 'LIST',
  recordId: section,
  label: TAB_SECTION_LABEL[section],
  pinned: true
});

export const initialTabState: TabState = { tabs: [], activeKey: '', pinnedVersion: 0, clock: 0 };

export const buildInitialTabState = (pathname: string): TabState => {
  const section = getSectionFromPath(pathname);
  if (!section) {
    return initialTabState;
  }

  const pinnedTab = buildPinnedTab(section);
  return { section, tabs: [pinnedTab], activeKey: pinnedTab.key, pinnedVersion: 0, clock: 1 };
};

export const tabReducer = (state: TabState, action: TabAction): TabState => {
  switch (action.type) {
    /**
     * Route landed on a different section: record tabs from the previous section are dropped and the
     * pinned tab is replaced (TAB-10). Returning the same state object when nothing changed keeps this
     * safe to call on every location change.
     */
    case 'SYNC_SECTION': {
      if (state.section === action.section) {
        return state;
      }

      if (!action.section) {
        return initialTabState;
      }

      const clock = state.clock + 1;
      const pinnedTab = buildPinnedTab(action.section);
      return { section: action.section, tabs: [pinnedTab], activeKey: pinnedTab.key, pinnedVersion: state.pinnedVersion, clock };
    }

    case 'OPEN_TAB': {
      const clock = state.clock + 1;
      const key = buildTabKey(action.recordType, action.recordId);
      const existing = state.tabs.find(tab => tab.key === key);

      // TAB-05: focus the tab that is already open, flash it, never create a duplicate. A link that
      // named a section still moves the open tab to it, or "View Jobs" would do nothing at all on a
      // client the user happens to have open already.
      const innerTab = action.innerTab === undefined ? undefined : { index: action.innerTab, at: clock };

      if (existing) {
        return {
          ...state,
          activeKey: key,
          flashKey: key,
          clock,
          tabs: state.tabs.map(tab => (tab.key === key ? { ...tab, innerTab: innerTab || tab.innerTab } : tab))
        };
      }

      let tabs = state.tabs;
      let section = state.section;
      let notice: string | undefined;

      /**
       * Records can be opened from places that have no tab bar of their own — the equipment list, a
       * search result, a notification. There is no pinned tab in those, so adopt the record's own
       * section: without it the bar holds a single closable tab, and closing it left nothing to
       * fall back to, which crashed the app.
       */
      if (!tabs.some(tab => tab.pinned)) {
        section = TAB_RECORD_SECTION[action.recordType];
        tabs = [buildPinnedTab(section), ...tabs];
      }

      /**
       * Soft cap: the tab next to the pinned one makes room, so tabs leave in the order they arrived.
       *
       * Least recently used was cleverer and less predictable — the tab that vanished depended on a
       * history of visits nobody was tracking, so it could be any of them. Oldest-first means the bar
       * always gives up its leftmost record, which is the one the user is most likely done with.
       * The active tab is still never evicted, however long ago it was opened.
       */
      if (tabs.length >= MAX_TABS) {
        const victim = tabs.find(tab => !tab.pinned && tab.key !== state.activeKey);

        if (victim) {
          tabs = tabs.filter(tab => tab.key !== victim.key);
          notice = `Tab limit of ${MAX_TABS} reached — ${victim.label} was closed.`;
        }
      }

      const newTab: TabItem = {
        key,
        type: action.recordType,
        recordId: action.recordId,
        label: action.label,
        pinned: false,
        innerTab
      };

      return { ...state, section, tabs: [...tabs, newTab], activeKey: key, flashKey: undefined, notice, clock };
    }

    case 'ACTIVATE_TAB': {
      if (state.activeKey === action.key || !state.tabs.some(tab => tab.key === action.key)) {
        return state;
      }

      return { ...state, activeKey: action.key, flashKey: undefined };
    }

    /** TAB-04: focus moves to the tab immediately to the left, falling back to the pinned tab. */
    case 'CLOSE_TAB': {
      const index = state.tabs.findIndex(tab => tab.key === action.key);
      if (index < 0 || state.tabs[index].pinned) {
        return state;
      }

      const tabs = state.tabs.filter(tab => tab.key !== action.key);

      // Defensive: a pinned tab should always remain, but a reducer that throws takes the whole app
      // down with it, so never assume there is a neighbour to focus.
      if (tabs.length === 0) {
        return { ...state, tabs, activeKey: '', flashKey: undefined };
      }

      const neighbour = tabs[Math.min(Math.max(0, index - 1), tabs.length - 1)];
      const activeKey = state.activeKey === action.key ? neighbour.key : state.activeKey;

      return { ...state, tabs, activeKey, flashKey: undefined };
    }

    /** TAB-06: saving a record updates its tab label when the name or id label changed. */
    case 'UPDATE_LABEL': {
      const target = state.tabs.find(tab => tab.key === action.key);
      if (!target || target.label === action.label) {
        return state;
      }

      return { ...state, tabs: state.tabs.map(tab => (tab.key === action.key ? { ...tab, label: action.label } : tab)) };
    }

    case 'CLEAR_RECORD_TABS': {
      const pinnedTab = state.tabs.find(tab => tab.pinned);
      if (!pinnedTab) {
        return state.tabs.length === 0 ? state : initialTabState;
      }

      return { ...state, tabs: [pinnedTab], activeKey: pinnedTab.key, flashKey: undefined };
    }

    case 'CLEAR_FLASH': {
      return state.flashKey ? { ...state, flashKey: undefined } : state;
    }

    /** Start over from the URL — used when the feature is switched on or off mid-session. */
    case 'RESET_TO_PATH': {
      return buildInitialTabState(action.pathname);
    }

    case 'CLEAR_NOTICE': {
      return state.notice ? { ...state, notice: undefined } : state;
    }

    /**
     * The list behind a record tab keeps its own data for as long as it stays mounted, so deleting a
     * record leaves it showing a row that is gone. Remounting the pinned tab is the honest fix.
     */
    case 'REFRESH_PINNED': {
      return { ...state, pinnedVersion: state.pinnedVersion + 1 };
    }

    case 'REFRESH_TAB': {
      if (!state.tabs.some(tab => tab.key === action.key)) {
        return state;
      }

      return { ...state, tabs: state.tabs.map(tab => (tab.key === action.key ? { ...tab, version: (tab.version || 0) + 1 } : tab)) };
    }

    default:
      return state;
  }
};

const defaultTabContext: TabContextData = {
  isTabNavActive: false,
  tabs: [],
  activeKey: '',
  canOpenTab: () => false,
  openTab: () => false,
  activateTab: () => {},
  closeTab: () => {},
  updateTabLabel: () => {},
  clearRecordTabs: () => {},
  dismissNotice: () => {},
  refreshPinnedTab: () => {},
  refreshTab: () => {},
  pinnedVersion: 0
};

export const TabContext = createContext<TabContextData>(defaultTabContext);

/** Where a tab lives in the address bar. */
const activeTabPath = (tab?: TabItem): string | undefined => {
  if (!tab) {
    return undefined;
  }

  return tab.pinned ? TAB_SECTION_PATH[tab.recordId as TabSection] : buildRecordPath(tab.type as TabRecordType, tab.recordId!);
};

export const TabProvider: FC = props => {
  const { children } = props;
  const { history, location } = useRouter();
  const { currentUser } = useContext(CurrentUserContext);
  const isTabNavActive = useTabNavActive();

  const [state, dispatch] = useReducer(tabReducer, location.pathname, buildInitialTabState);

  const pathSection = getSectionFromPath(location.pathname);

  /**
   * Only a list URL changes the section, never a record URL.
   *
   * Opening a quotation from the job list moves the address bar to /quotations/1042, but the user is
   * still working through jobs — treating that as "left the Jobs section" would throw away every tab
   * they had open, which is exactly what it used to do.
   */
  const isRecordPath = !!matchRecordPath(location.pathname);

  const tabsRef = useRef(state.tabs);
  tabsRef.current = state.tabs;

  const lastPathRef = useRef(location.pathname);
  const lastActiveKeyRef = useRef(state.activeKey);
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (isRecordPath) {
      return;
    }

    dispatch({ type: 'SYNC_SECTION', section: pathSection });
  }, [isRecordPath, pathSection]);

  const canOpenTab = useCallback(
    (recordType: TabRecordType) => {
      // Deny while the user is still loading: getCurrentRoleGrants falls back to dummyRoleGrants,
      // which grants ACCESS to several modules, and that fallback must never decide a tab.
      if (!currentUser) {
        return false;
      }

      // Tabs render detail pages outside <Route>, so they must repeat the ConditionalRoute guard.
      return hasAccessPermission(TAB_RECORD_MODULE[recordType], 'ACCESS', getCurrentRoleGrants(currentUser));
    },
    [currentUser]
  );

  const openTab = useCallback(
    ({ recordType, recordId, label, innerTab }: OpenTabParams) => {
      // A record type that TabHost cannot render yet must never become a tab, or the tab would open
      // onto content the route is still responsible for.
      if (!isTabNavActive || !isTabHostRecordType(recordType) || !canOpenTab(recordType)) {
        return false;
      }

      const id = String(recordId);
      dispatch({ type: 'OPEN_TAB', recordType, recordId: id, label: buildTabLabel(recordType, id, label), innerTab });
      return true;
    },
    [canOpenTab, isTabNavActive]
  );

  const activateTab = useCallback((key: string) => dispatch({ type: 'ACTIVATE_TAB', key }), []);
  const closeTab = useCallback((key: string) => dispatch({ type: 'CLOSE_TAB', key }), []);
  const updateTabLabel = useCallback((key: string, label: string) => dispatch({ type: 'UPDATE_LABEL', key, label }), []);
  const clearRecordTabs = useCallback(() => dispatch({ type: 'CLEAR_RECORD_TABS' }), []);
  const dismissNotice = useCallback(() => dispatch({ type: 'CLEAR_NOTICE' }), []);
  const refreshPinnedTab = useCallback(() => dispatch({ type: 'REFRESH_PINNED' }), []);
  const refreshTab = useCallback((key: string) => dispatch({ type: 'REFRESH_TAB', key }), []);

  /**
   * Point the tabs at whatever the URL says.
   *
   * A record URL becomes a tab — that covers bookmarks, Ctrl+click landings, and links from outside
   * the app. A section URL focuses the pinned list tab, which is what makes the sidebar work: the
   * user asked for the list, so the list is what they get.
   */
  const syncTabsToPath = useCallback(
    (pathname: string) => {
      const record = matchRecordPath(pathname);

      if (record && isTabHostRecordType(record.recordType)) {
        const key = buildTabKey(record.recordType, record.recordId);

        // Focus without the flash cue: the user navigated here, they did not ask for a tab twice.
        if (tabsRef.current.some(tab => tab.key === key)) {
          dispatch({ type: 'ACTIVATE_TAB', key });
        } else {
          openTab(record);
        }

        return;
      }

      const pathSection = getSectionFromPath(pathname);
      if (pathSection) {
        dispatch({ type: 'ACTIVATE_TAB', key: buildTabKey('LIST', pathSection) });
      }
    },
    [openTab]
  );

  /**
   * The user or their admin switched the feature. Throw the tab state away and rebuild it from the
   * URL, so turning it back on later never resurrects tabs from a previous session.
   */
  const isFirstSettingRef = useRef(true);
  useEffect(() => {
    if (isFirstSettingRef.current) {
      isFirstSettingRef.current = false;
      return;
    }

    dispatch({ type: 'RESET_TO_PATH', pathname: location.pathname });
    hasSyncedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabNavActive]);

  /**
   * Keeps the URL and the active tab agreeing without the two fighting over it.
   *
   * Whichever of them just changed is the one that wins. The URL moving means the user navigated —
   * a sidebar click, Back, a pasted link — so the tabs follow it. The active tab moving means the
   * user clicked a tab, so the URL follows instead, with replace so Back stays useful.
   */
  useEffect(() => {
    const pathChanged = lastPathRef.current !== location.pathname;
    const activeChanged = lastActiveKeyRef.current !== state.activeKey;

    lastPathRef.current = location.pathname;
    lastActiveKeyRef.current = state.activeKey;

    if (pathChanged || !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      syncTabsToPath(location.pathname);
      return;
    }

    if (!activeChanged) {
      return;
    }

    const path = activeTabPath(state.tabs.find(tab => tab.key === state.activeKey));
    if (path && path !== location.pathname) {
      history.replace(path);
    }
  }, [history, location.pathname, state.activeKey, state.tabs, syncTabsToPath]);

  // The flash cue is a one shot signal; clear it once the tab bar has had a chance to render it.
  useEffect(() => {
    if (!state.flashKey) {
      return;
    }

    const timer = window.setTimeout(() => dispatch({ type: 'CLEAR_FLASH' }), 1000);
    return () => window.clearTimeout(timer);
  }, [state.flashKey]);

  const value = useMemo<TabContextData>(
    () => ({
      isTabNavActive,
      section: state.section,
      tabs: state.tabs,
      activeKey: state.activeKey,
      flashKey: state.flashKey,
      activeTab: state.tabs.find(tab => tab.key === state.activeKey),
      canOpenTab,
      openTab,
      activateTab,
      closeTab,
      updateTabLabel,
      clearRecordTabs,
      notice: state.notice,
      dismissNotice,
      refreshPinnedTab,
      refreshTab,
      pinnedVersion: state.pinnedVersion
    }),
    [activateTab, canOpenTab, clearRecordTabs, closeTab, dismissNotice, isTabNavActive, openTab, refreshPinnedTab, refreshTab, state, updateTabLabel]
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

export const useTabs = (): TabContextData => useContext(TabContext);

/**
 * True when the page asking is the tab the user is looking at.
 *
 * Detail pages use it to hold back portalled UI — dialogs, drawers, snackbars — because those render
 * into document.body and would otherwise stay on screen after the user switches to another tab.
 * Defaults to true so a page rendered by a plain route behaves exactly as it does today.
 */
export const TabActiveContext = createContext<boolean>(true);

export const TabActiveProvider = TabActiveContext.Provider;

export const useIsTabActive = (): boolean => useContext(TabActiveContext);
